import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  CloseRounded as CloseIcon,
  FlashlightOnRounded as TorchOnIcon,
  FlashlightOffRounded as TorchOffIcon,
  CheckCircleRounded as OkIcon,
  ErrorRounded as FailIcon,
  QrCodeScannerRounded as ScanIcon,
} from "@mui/icons-material";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

// ─── Design tokens (shared look with the pawn screens) ───────────────────────
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const INK = "#0F172A";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const SUCCESS = "#059669";
const DANGER = "#DC2626";

// html5-qrcode needs a plain DOM id to mount the <video> into.
const SCANNER_ELEMENT_ID = "pawn-barcode-scanner-region";

// Pouch stickers carry a Code128 of the voucher number (see paymentScreen/
// barcode.js). Restricting the decoder to the 1D symbologies we actually print
// — plus QR, so a QR sticker still works — keeps each frame cheap to decode.
const FORMATS = [
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.CODABAR,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.QR_CODE,
];

// A 1D barcode is wide and short, so the scan window is too — a square box
// forces the user to hold the phone much further back than necessary.
const qrbox = (viewW, viewH) => ({
  width: Math.floor(viewW * 0.92),
  height: Math.max(80, Math.floor(Math.min(viewH * 0.45, 190))),
});

const beep = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = 880;
    gain.gain.value = 0.04;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.11);
    setTimeout(() => ctx.close?.(), 400);
  } catch {
    // Audio feedback is a nicety — never let it break a scan.
  }
};

const vibrate = () => {
  try {
    navigator.vibrate?.(60);
  } catch {
    /* no-op */
  }
};

// html5-qrcode rejects with bare strings in most of its failure paths (see
// Html5Qrcode.start / its constructor), so `err.message` is undefined there.
// Without this the real reason gets swallowed by the generic fallback.
const errorText = (err) => {
  if (!err) return "";
  if (typeof err === "string") return err;
  return err.message || String(err);
};

/**
 * Full-screen (on phones) camera barcode scanner.
 *
 * Stays open after a hit so a whole tray of pouches can be scanned in one go —
 * every decoded value is handed to `onDetected`, which returns
 * `{ ok, message }` so the result can be echoed in the on-screen feed.
 */
const BarcodeScannerDialog = ({
  open,
  onClose,
  onDetected,
  title = "Scan Barcode",
  hint = "Hold the pouch barcode inside the frame.",
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [feed, setFeed] = useState([]);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  // The <video> host node, captured by ref callback. MUI's Portal renders null
  // on the first pass after `open` flips (it resolves its mount node in a
  // layout effect), so `open` alone does NOT mean the node is in the document —
  // starting off it made html5-qrcode throw "HTML Element with id=… not found".
  const [containerEl, setContainerEl] = useState(null);
  // Bumped by Retry to re-run the start effect after a failure.
  const [attempt, setAttempt] = useState(0);

  const scannerRef = useRef(null);
  // The decode callback is captured once by html5-qrcode at start(), so route
  // it through a ref to always run the latest closure.
  const onScanRef = useRef(null);
  const busyRef = useRef(false);
  const lastCodeRef = useRef("");
  const lastAtRef = useRef(0);

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (!scanner) return;
    try {
      await scanner.stop();
    } catch {
      // Already stopped / never started — nothing to release.
    }
    try {
      scanner.clear();
    } catch {
      /* no-op */
    }
  }, []);

  const handleDecoded = useCallback(
    async (decodedText) => {
      const code = (decodedText || "").trim();
      if (!code || busyRef.current) return;
      // The decoder fires many times a second on the same sticker — ignore a
      // repeat of the last code inside a short window.
      const now = Date.now();
      if (code === lastCodeRef.current && now - lastAtRef.current < 2500) return;
      lastCodeRef.current = code;
      lastAtRef.current = now;

      busyRef.current = true;
      setBusy(true);
      beep();
      vibrate();
      try {
        const res = (await onDetected?.(code)) || {};
        setFeed((prev) =>
          [
            {
              key: `${code}-${now}`,
              code,
              ok: res.ok !== false,
              message: res.message || "",
            },
            ...prev,
          ].slice(0, 5),
        );
      } catch (err) {
        setFeed((prev) =>
          [
            {
              key: `${code}-${now}`,
              code,
              ok: false,
              message: err?.message || "Lookup failed.",
            },
            ...prev,
          ].slice(0, 5),
        );
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [onDetected],
  );

  useEffect(() => {
    onScanRef.current = handleDecoded;
  }, [handleDecoded]);

  // Fresh feed each time the dialog is opened (kept out of the camera effect,
  // which also re-runs on Retry).
  useEffect(() => {
    if (open) setFeed([]);
  }, [open]);

  // ── Camera lifecycle, tied to `open` ───────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setError("");
    setTorchOn(false);
    setTorchSupported(false);
    lastCodeRef.current = "";
    busyRef.current = false;
    setBusy(false);
    setStarting(true);

    // Wait for the portal to actually put the host node in the document.
    if (!containerEl) return undefined;

    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error(
            window.isSecureContext === false
              ? "The camera needs a secure (HTTPS) connection. Open this page over HTTPS, or type the voucher number instead."
              : "This device / browser does not expose a camera to the page.",
          );
        }
        const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID, {
          formatsToSupport: FORMATS,
          // Uses the browser's native BarcodeDetector where available (Android
          // Chrome), which is markedly faster than the JS decoder.
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
          verbose: false,
        });
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: "environment" },
          { fps: 12, qrbox, disableFlip: true },
          (text) => onScanRef.current?.(text),
          () => {
            // Fires for every frame without a barcode — expected, ignore.
          },
        );

        if (cancelled) {
          await stopScanner();
          return;
        }
        setStarting(false);

        try {
          const caps = scanner.getRunningTrackCapabilities?.() || {};
          setTorchSupported(!!caps.torch);
        } catch {
          /* torch is optional */
        }
      } catch (err) {
        if (cancelled) return;
        setStarting(false);
        const name = typeof err === "object" ? err?.name || "" : "";
        const raw = errorText(err);
        setError(
          name === "NotAllowedError" || name === "SecurityError"
            ? "Camera permission was denied. Allow camera access for this site in the browser settings, then try again."
            : name === "NotFoundError" || name === "OverconstrainedError"
              ? "No usable camera was found on this device."
              : name === "NotReadableError"
                ? "The camera is already in use by another app. Close it and try again."
                : raw || "Could not start the camera.",
        );
        await stopScanner();
      }
    };

    start();

    return () => {
      cancelled = true;
      stopScanner();
    };
  }, [open, containerEl, attempt, stopScanner]);

  const toggleTorch = async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    const next = !torchOn;
    try {
      await scanner.applyVideoConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch {
      setTorchSupported(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontWeight: 800,
          color: INK,
          fontSize: "1.05rem",
          pb: 1.5,
        }}
      >
        <ScanIcon sx={{ color: BLUE }} />
        <Box sx={{ flexGrow: 1 }}>{title}</Box>
        {torchSupported && (
          <IconButton onClick={toggleTorch} size="small">
            {torchOn ? (
              <TorchOnIcon sx={{ color: "#B45309" }} />
            ) : (
              <TorchOffIcon sx={{ color: INK_MUTED }} />
            )}
          </IconButton>
        )}
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ color: INK_MUTED }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 1.5, sm: 2.5 }, pb: 1 }}>
        {/* Camera viewport — html5-qrcode mounts its <video> in here. */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            minHeight: 240,
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "#0B1220",
            border: `1px solid ${BORDER}`,
            "& video": { width: "100% !important", display: "block" },
          }}
        >
          <Box
            id={SCANNER_ELEMENT_ID}
            ref={setContainerEl}
            sx={{ width: "100%" }}
          />

          {(starting || error) && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                px: 3,
                textAlign: "center",
              }}
            >
              {error ? (
                <>
                  <FailIcon sx={{ color: DANGER, fontSize: 34 }} />
                  <Typography sx={{ color: "#E2E8F0", fontSize: "0.85rem" }}>
                    {error}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setAttempt((a) => a + 1)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.5)",
                      "&:hover": { borderColor: "#fff" },
                    }}
                  >
                    Try again
                  </Button>
                </>
              ) : (
                <>
                  <CircularProgress size={26} sx={{ color: "#fff" }} />
                  <Typography sx={{ color: "#CBD5E1", fontSize: "0.8rem" }}>
                    Starting the camera…
                  </Typography>
                </>
              )}
            </Box>
          )}

          {busy && !error && (
            <Chip
              size="small"
              icon={<CircularProgress size={12} sx={{ color: "#fff" }} />}
              label="Loading…"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: "rgba(15,23,42,0.75)",
                color: "#fff",
                fontWeight: 700,
                "& .MuiChip-icon": { color: "#fff", ml: 1 },
              }}
            />
          )}
        </Box>

        <Typography
          sx={{ mt: 1.25, fontSize: "0.78rem", color: INK_MUTED, textAlign: "center" }}
        >
          {error ? "Type the voucher number in the search box instead." : hint}
        </Typography>

        {/* Rolling feed of what each scan resolved to. */}
        {feed.length > 0 && (
          <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
            {feed.map((f) => (
              <Box
                key={f.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1,
                  py: 0.75,
                  borderRadius: 1.5,
                  bgcolor: f.ok ? "#ECFDF5" : "#FEF2F2",
                }}
              >
                {f.ok ? (
                  <OkIcon sx={{ fontSize: 16, color: SUCCESS }} />
                ) : (
                  <FailIcon sx={{ fontSize: 16, color: DANGER }} />
                )}
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 800,
                    color: INK,
                    whiteSpace: "nowrap",
                  }}
                >
                  {f.code}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: f.ok ? SUCCESS : DANGER,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {f.message}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 1.5, sm: 2.5 }, pb: 2 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={onClose}
          sx={{
            bgcolor: BLUE,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            "&:hover": { bgcolor: BLUE_DARK, boxShadow: "none" },
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BarcodeScannerDialog;
